<template>
  <section class="bg-white rounded mb-4 p-3 p-sm-4">
    <h2 class="mb-sm-3">Sire Award Eligibility</h2>
    <div class="row align-items-start">
      <div class="col-12 col-md-auto form-group">
        <label for="year">Year</label>
        <select id="year" class="form-control pr-5" v-model="selectedYear" @change="$nextTick(() => {getSires()})">
          <option v-for="(year, yearIndex) in years" :key="yearIndex" :value="year.id">{{year.name}}</option>
        </select>
        <small>Changing the year will save the current list.</small>
      </div>
    </div>

    <div class="form-row align-items-start" v-for="(sire, sireIndex) in selectedHorses" :key="sireIndex">
      <div class="col-12 col-md form-group">
        <label :for="'horse' + sireIndex">Horse</label>
        <div :id="'horsediv' + sireIndex">
          <horse-search-select
            :element-id="'horse' + sireIndex"
            :value="selectedHorses[sireIndex] || []"
            @input="v => $set(selectedHorses, sireIndex, v)"
          />
        </div>
      </div>
      <div :class="['col-12 col-md form-group pb-lg-1 pl-md-0', isLegacy() ? 'col-lg-1' : 'flex-grow-0']">
        <label aria-hidden="true" class="d-none d-md-block">&nbsp;</label>
        <button @click.prevent="removeResult(sireIndex)" aria-label="Remove Result" class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1 mr-n-2 d-none d-md-block">
          <div class="btn-remove">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17"><g fill="currentColor"><path d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/><path d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/><path d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/></g></svg>
          </div>
        </button>
        <button class="btn btn-block-mob btn-outline-primary d-md-none" @click="removeResult(sireIndex)" aria-label="Remove Result">Remove</button>
      </div>
    </div>
    <div class="col-12 col-md form-group align-self-end" v-if="selectedHorses.length === 0">
      <div class="text-center py-2 m-auto">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>

    <div class="d-lg-flex justify-content-end mt-3">
      <button class="btn btn-block-mob btn-outline-primary mr-lg-2" @click.prevent="addAnotherResult();focusNew()">Add Another Sire</button>
      <button :class="['btn btn-block-mob', processing ? 'btn-primary-dark' : 'btn-primary']" @click="saveSires(selectedYear)">
        {{ !processing ? 'Save' : '' }}
        <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
      </button>
    </div>
    <div role="alert" class="alert alert-success mt-3 mb-0" v-if="sireSuccess">{{sireSuccess}}</div>
    <div role="alert" class="alert alert-warning mt-3 mb-0" v-if="sireError">{{sireError}}</div>
  </section>
</template>

<script>
import axios from 'axios'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import HorseSearchSelect from '@/components/Horses/HorseSearchSelect'

export default {
  components: {
    'horse-search-select': HorseSearchSelect
  },
  mixins: [LEGACYBROWSERS],
  data() {
    return {
      selectedHorses: [],
      selectedYear: 0,
      years: [],
      siresHaveChanged: false,
      processing: false,
      sireSuccess: '',
      sireError: ''
    }
  },
  methods: {
    saveSires(theYear) {
      this.processing = true
      this.sireSuccess = ''
      this.sireError = ''
      axios.post(`${this.$api}/admin/sire-eligibility`, { year: theYear, horseIds: this.selectedHorses.flat(1) }).then((response) => {
        this.$nextTick(() => {
          this.siresHaveChanged = false
        })
        this.processing = false
        if (response.data.status === 'success') {
          this.sireSuccess = `Sire List for ${theYear} saved successfully.`
        } else {
          this.sireError = `Please check the data in your submission.`
        }
      }).catch(() => {
        this.processing = false
        this.sireError = `Please check the data in your submission.`
      })
    },
    getSires() {
      this.selectedHorses = []
      this.$nextTick(() => {
        this.siresHaveChanged = false
      })
      this.sireSuccess = ''
      this.sireError = ''
      axios.get(`${this.$api}/admin/sire-eligibility`, { params: { year: this.selectedYear === 0 ? null : this.selectedYear } }).then((response) => {
        if (response.data.status === 'success') {
          this.selectedHorses = response.data.data.map(horse => [horse.horse_id.toString()])
          this.addAnotherResult()
          setTimeout(() => {
            this.siresHaveChanged = false
          }, 100)
        } else {
          this.sireError = `Sire list retrieval failed. Please try again later.`
        }
      }).catch(() => {
        this.sireError = `Sire list retrieval failed. Please try again later.`
      })
    },
    focusNew() {
      setTimeout(() => {
        // HorseSearchSelect only renders its <input> while active, so a freshly added
        // row has no <input> yet - focus its always-present .multiselect root instead,
        // which triggers HorseSearchSelect's own focusin handling to activate + focus it.
        const target = document.body.querySelector(`#horsediv${this.selectedHorses.length - 1} .multiselect`)
        if (target) target.focus()
      }, 10)
    },
    removeResult(index) {
      this.selectedHorses.splice(index, 1)
    },
    addAnotherResult() {
      this.selectedHorses.push(null)
      /* Workaround for VueJS reactivity bug - scheduled to be fixed in VueJS 3. */
      this.$forceUpdate()
      this.$nextTick(() => {
        this.siresHaveChanged = false
      })
    }
  },
  watch: {
    selectedYear(newVal, oldVal) {
      if (newVal !== oldVal && oldVal !== 0 && this.siresHaveChanged) {
        this.siresHaveChanged = false
        this.saveSires(oldVal)
      }
    },
    selectedHorses: {
      deep: true,
      handler() {
        this.siresHaveChanged = true
      }
    }
  },
  mounted() {
    /* Get year data */
    const today = this.$store.state.currentDate ? new Date(this.$store.state.currentDate) : new Date()
    this.selectedYear = today.getFullYear()

    for (let Y = this.selectedYear; Y >= 2015; Y--) {
      this.years.push({ id: Y, name: `${Y} / ${Y + 1}` })
    }

    /* Get sire data */
    this.getSires()
  }
}
</script>


// WEBPACK FOOTER //
// src/views/Titles/Edit/TitleEditDetailsSire.vue